import React, { createContext, useContext, useMemo } from 'react';

import { type ProgressiveEntryRevealResult } from './use-progressive-entry-reveal.ts';

interface ProgressiveEntryRevealContextValue {
  activeEntryId?: string;
  result: ProgressiveEntryRevealResult;
  revealedEntryIds: ReadonlySet<string>;
}

const ProgressiveEntryRevealContext = createContext<
  ProgressiveEntryRevealContextValue | undefined
>(undefined);

export function ProgressiveEntryRevealProvider({
  activeEntryId,
  children,
  result
}: {
  activeEntryId?: string;
  children: React.ReactNode;
  result: ProgressiveEntryRevealResult;
}) {
  const revealedEntryIds = useMemo(() => new Set(result.entryIds), [result.entryIds]);
  const value = useMemo(
    () => ({ activeEntryId, result, revealedEntryIds }),
    [activeEntryId, result, revealedEntryIds]
  );

  return (
    <ProgressiveEntryRevealContext.Provider value={value}>
      {children}
    </ProgressiveEntryRevealContext.Provider>
  );
}

export function useProgressiveEntryRevealContext() {
  return useContext(ProgressiveEntryRevealContext);
}

export function useProgressivelyRevealedEntryIdSet(): ReadonlySet<string> | undefined {
  return useProgressiveEntryRevealContext()?.revealedEntryIds;
}

export function useProgressivelyRevealedEntryIds(entryIds: string[]): string[] {
  const context = useProgressiveEntryRevealContext();

  return useMemo(
    () =>
      context
        ? entryIds.filter((entryId) => context.revealedEntryIds.has(entryId))
        : entryIds,
    [context, entryIds]
  );
}
